<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";

import type { Headers, PaginationMeta } from "@/types/rest";
import type { ActivityGroup } from "@/types/activityGroup";

import { activityGroupsStore } from "@/stores/activityGroups";
import { addErrorBottom } from "@/stores/messages";

import { useComponentErrorCapture } from "@/mixins/error";
import { useTiming } from "@/mixins/timing";
import { useDataHelper } from "@/mixins/dataHelper";
import { getPaginationLimit } from "@/mixins/listing";

import ActivityGroupListItem from "@/components/activity-group/ActivityGroupListItem.vue";
import ListPaging from "@/components/ui/ListPaging.vue";

const router = useRouter();
const { sleep } = useTiming();
const { objectsDeepEqual } = useDataHelper();
useComponentErrorCapture();

const activityGroupsStorage = activityGroupsStore();

const props = defineProps({
  pagination: {
    type: Object,
    default: {},
  },
});

const emit = defineEmits(["add", "loaded"]);

const activityGroups = ref<ActivityGroup[] | []>([]);
const pagination = ref<PaginationMeta>(
  Object.keys(props.pagination).length > 0 ?
  props.pagination as PaginationMeta :
  { limit: getPaginationLimit() ?? 20, page: 1 }
);

const fetching = ref(false);

activityGroupsStorage.$subscribe(async (mutation, state) => {
  await sleep(1000);
  if (!objectsDeepEqual(state.activityGroupsByPage.activityGroups, activityGroups.value)) {
    try {
      await fetchActivityGroups();
    } catch (e: any) {
      addErrorBottom(e);
    }
  }
});

async function fetchActivityGroups() {
  fetching.value = true;
  try {
    const activityGroupList = await activityGroupsStorage.getActivityGroupsByPageByLimit(pagination.value.page, pagination.value.limit);
    if (activityGroupList && activityGroupList.data) {
      activityGroups.value = activityGroupList.data.activityGroups;

      const activityGroupHeaders = activityGroupList.headers as Headers;
      if (activityGroupHeaders) {
        const headerLimit = Number(activityGroupHeaders['pagination-limit']);
        const newLimit = headerLimit <= Number(activityGroupHeaders['pagination-count']) ? pagination.value.limit : headerLimit;
        pagination.value = {
          count: Number(activityGroupHeaders['pagination-count']) || 0,
          limit: newLimit || 0,
          page: Number(activityGroupHeaders['pagination-page']) || 0,
        }
      }
    }
    emit('loaded', pagination.value);
  } catch (e: any) {
    addErrorBottom(e);
  }
  fetching.value = false;
}

function goToItem(id: string) {
  router.push({
    name: "activity-group",
    params: { group_id: id },
    query: { category: 'activity-group' },
  });
}

function changePage(value: number) {
  pagination.value.page = value;
  fetchActivityGroups();
}

function changePerPage(value: number) {
  pagination.value.page = 1;
  pagination.value.limit = value;
  fetchActivityGroups();
}

async function deleteItem(activityGroup: ActivityGroup) {
  try {
    const lastActiveListItem = (activityGroups.value.length == 1 && pagination.value.page > 1);
    await activityGroupsStorage.delete(activityGroup);

    if (lastActiveListItem) {
      changePage(pagination.value.page - 1);
    } else {
      fetchActivityGroups();
    }
  } catch (e: any) {
    addErrorBottom(e);
  }
}

function addItem() {
  emit('add');
}

onMounted(() => {
  fetchActivityGroups();
})

</script>
<template>
  <div class="ent-list ent-list--activity-group">
    <div v-if="activityGroups && activityGroups.length > 0" class="ent-list__body-wrapper" v-overflow-indicator>
      <div class="ent-list__body">
        <ActivityGroupListItem
          v-for="activityGroup in activityGroups"
          @goto="goToItem(activityGroup.group_id)"
          @delete="deleteItem(activityGroup)"
          :key="activityGroup.group_id"
          :activity-group="activityGroup"
        />
      </div>
    </div>
    <ListPaging
      v-if="activityGroups && activityGroups.length > 0"
      @change-page="changePage"
      @change-per-page="changePerPage"
      :pagination="pagination || {}"
      :length="activityGroups.length"
    />
    <div v-if="!fetching && activityGroups.length < 1" class="ent-list__no-items">
      <img alt="Add activity group" src="/images/add-page.svg" />
      <h3>{{ $t('activity_group.add_first') }}</h3>
      <p>{{ $t('activity_group.no_activity_groups') }}</p>

      <button
        @click="addItem"
        class="button button--primary button--hybrid button--hybrid--reversed">
        {{ $t('ui.add') }}
        <i class="fa-light fa-plus"></i>
      </button>
    </div>
  </div>
</template>
